import PropTypes from 'prop-types';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import AppScrollArea from '../../../../components/common/AppScrollArea';
import EventModalMediaContent from '../../../../components/EventModalMediaContent';
import SubtleBadge from '../../../../components/common/SubtleBadge';
import Loader from '../../../../components/Loader';
import OrderApprovalHistory from '../../../order/OrderApprovalHistory';
import { useGetCustomerApprovalHistoryQuery } from '../../../../slices/approvalsApiSlice';
import formatCurrency from '../../../../utils/formatCurrency';
import { getActualCreatorHighlight } from '../../../../utils/approval/approvalListHub';

const STATUS_META = {
	waitingApproval: { bg: 'warning', text: '待审批' },
	reject: { bg: 'danger', text: '已驳回' },
	approved: { bg: 'success', text: '已通过' },
	withdrawn: { bg: 'secondary', text: '已撤回' },
};

const HISTORY_ASIDE_SCROLL_MAX = 'min(24rem, calc(90vh - 14rem))';

const formatDateTime = (date) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');

const quotaKindLabel = (kind) => {
	if (kind === 'temporary') return '临时额度';
	if (kind === 'permanent') return '永久额度';
	return '-';
};

/** 待办里查看额度申请：审批侧可同意/不同意，申请侧可撤回 */
const QuotaApplyInfoModal = ({ show, onHide, approval, onWithdraw, onRenew, onReedit, onCancel, onApprove, onReject, isApproving, isRejecting }) => {
	const { isDark } = useSelector((state) => state.config);
	const detail = approval?.detail || {};
	const statusMeta = STATUS_META[approval?.status] || { bg: 'secondary', text: approval?.status || '额度申请' };
	const isRejected = approval?.status === 'reject';
	const isWithdrawn = approval?.status === 'withdrawn';
	const isWaiting = approval?.status === 'waitingApproval';
	const canDecide = isWaiting && (onApprove || onReject);
	const title = String(approval?.title || '').replace(/<[^>]+>/g, '') || detail.customerName || '额度申请';
	const applicantName = approval?.applicant?.name || '';
	const assistantName = approval?.assistant?.name || '';
	const creatorHighlight = getActualCreatorHighlight({
		entryKind: 'quota',
		applicant: approval?.applicant,
		assistant: approval?.assistant,
	});
	const approvalId = approval?._id;
	const {
		data: fetchedHistory,
		isLoading: isHistoryLoading,
		isFetching: isHistoryFetching,
		isError: isHistoryError,
		isSuccess: isHistorySuccess,
	} = useGetCustomerApprovalHistoryQuery(approvalId, {
		skip: !show || !approvalId,
	});
	const historyReady = !show || !approvalId || isHistorySuccess || isHistoryError || (!isHistoryLoading && !isHistoryFetching);
	const history = Array.isArray(fetchedHistory) ? fetchedHistory : [];
	const actionBusy = Boolean(isApproving || isRejecting);
	const amountText = detail.applyQuota != null && detail.applyQuota !== '' ? formatCurrency(detail.applyQuota) : '-';
	const termText =
		detail.quotaKind === 'temporary'
			? detail.effectiveFrom && detail.effectiveTo
				? `${detail.effectiveFrom} 至 ${detail.effectiveTo}`
				: '-'
			: detail.repaymentTermDays != null && detail.repaymentTermDays !== ''
				? `${detail.repaymentTermDays} 天`
				: '-';

	return (
		<Modal show={show} onHide={onHide} contentClassName='border' centered size='lg'>
			<Modal.Header closeButton closeVariant={isDark ? 'white' : undefined} className='bg-body-tertiary px-x1 border-bottom-0'>
				{historyReady ? (
					<Modal.Title className='min-w-0'>
						<h5 className='mb-0 d-flex align-items-center gap-2 flex-nowrap min-w-0'>
							<span className='text-truncate' title={title}>
								{title}
							</span>
							<SubtleBadge bg={statusMeta.bg} pill className='flex-shrink-0'>
								{statusMeta.text}
							</SubtleBadge>
						</h5>
					</Modal.Title>
				) : (
					<div className='flex-grow-1' aria-hidden='true' />
				)}
			</Modal.Header>
			{historyReady ? (
				<>
					<Modal.Body className='p-0 fs-10'>
						<div className='row g-0 align-items-stretch'>
							<div className='col-12 col-md min-w-0 px-x1 pb-x1 pt-1'>
								<EventModalMediaContent icon='coins' heading='额度信息'>
									<div className='fs-10'>
										<p className='mb-2'>
											<strong>客户名称：</strong>
											{detail.customerName || '-'}
										</p>
										<p className='mb-2'>
											<strong>额度类型：</strong>
											{quotaKindLabel(detail.quotaKind)}
										</p>
										<p className='mb-2'>
											<strong>申请额度：</strong>
											{amountText}
										</p>
										<p className='mb-2'>
											<strong>{detail.quotaKind === 'temporary' ? '生效区间：' : '还款账期：'}</strong>
											{termText}
										</p>
										<p className='mb-0'>
											<strong>申请说明：</strong>
											{detail.rawReason || '-'}
										</p>
									</div>
								</EventModalMediaContent>

								<EventModalMediaContent icon='user' heading='申请人'>
									<div className='fs-10'>
										<p className='mb-2'>
											<strong>申请人：</strong>
											<span className={creatorHighlight.applicant ? 'text-primary' : undefined}>{applicantName || '-'}</span>
										</p>
										<p className='mb-2'>
											<strong>销售助理：</strong>
											<span className={creatorHighlight.assistant ? 'text-primary' : undefined}>{assistantName || '-'}</span>
										</p>
										<p className='mb-0'>
											<strong>申请时间：</strong>
											{formatDateTime(approval?.createdAt)}
										</p>
									</div>
								</EventModalMediaContent>
							</div>
							<aside className='col-12 col-md-4 col-lg-3 border-top border-md-top-0 border-md-start border-200 p-3'>
								<AppScrollArea className='app-scroll-area--embedded' maxHeight={HISTORY_ASIDE_SCROLL_MAX}>
									{history.length > 0 ? (
										<OrderApprovalHistory history={history} titleRow className='mb-0' />
									) : (
										<p className='mb-0 fs-10 text-500'>暂无审批历史</p>
									)}
								</AppScrollArea>
							</aside>
						</div>
					</Modal.Body>
					<Modal.Footer className='bg-body-tertiary px-x1 border-top-0'>
						<Button variant='falcon-default' size='sm' className='me-auto' onClick={onHide} disabled={actionBusy}>
							关闭
						</Button>
						{isWaiting && onWithdraw ? (
							<Button variant='primary' size='sm' onClick={onWithdraw} disabled={actionBusy}>
								<FontAwesomeIcon icon='undo' className='fs-11 me-2' />
								撤回
							</Button>
						) : null}
						{isRejected && onCancel ? (
							<Button variant='falcon-default' size='sm' onClick={onCancel} disabled={actionBusy}>
								<FontAwesomeIcon icon='ban' className='fs-11 me-2' />
								取消申请
							</Button>
						) : null}
						{isWithdrawn && onReedit ? (
							<Button variant='primary' size='sm' onClick={onReedit} disabled={actionBusy}>
								<FontAwesomeIcon icon='edit' className='fs-11 me-2' />
								再次编辑
							</Button>
						) : null}
						{isRejected && onRenew ? (
							<Button variant='falcon-primary' size='sm' onClick={onRenew} disabled={actionBusy}>
								<FontAwesomeIcon icon='history' className='fs-11 me-2' />
								再次申请
							</Button>
						) : null}
						{canDecide ? (
							actionBusy ? (
								<Spinner variant='primary' size='sm' />
							) : (
								<>
									{onApprove ? (
										<Button variant='success' size='sm' onClick={onApprove}>
											{isApproving ? <Spinner size='sm' className='me-1' /> : <FontAwesomeIcon icon='check' className='me-1' />}
											同意
										</Button>
									) : null}
									{onReject ? (
										<Button variant='danger' size='sm' onClick={onReject}>
											{isRejecting ? <Spinner size='sm' className='me-1' /> : <FontAwesomeIcon icon='times' className='me-1' />}
											不同意
										</Button>
									) : null}
								</>
							)
						) : null}
					</Modal.Footer>
				</>
			) : (
				<Modal.Body className='px-x1 modal-dots-loading'>
					<Loader />
				</Modal.Body>
			)}
		</Modal>
	);
};

QuotaApplyInfoModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func.isRequired,
	approval: PropTypes.object,
	onWithdraw: PropTypes.func,
	onRenew: PropTypes.func,
	onReedit: PropTypes.func,
	onCancel: PropTypes.func,
	onApprove: PropTypes.func,
	onReject: PropTypes.func,
	isApproving: PropTypes.bool,
	isRejecting: PropTypes.bool,
};

export default QuotaApplyInfoModal;
